import { cn } from '../lib/utils';
import { useUiStrings } from '../lib/ui-strings';

export type SiteId = 'cn' | 'global';

const SITE = {
  cn: { zh: '国内站', en: 'China', bar: 'bg-site-cn', dot: 'bg-site-live', text: 'text-site-cn' },
  global: { zh: '海外站', en: 'Global', bar: 'bg-site-global', dot: 'bg-site-live', text: 'text-site-global' },
} as const;

/** 顶部站点色条：三端一致，永远可见（docs/07 §1）。 */
export function SiteBar({ site, className }: { site: SiteId; className?: string }) {
  return <div className={cn('h-1 w-full', SITE[site].bar, className)} aria-hidden />;
}

export function SiteTag({
  site,
  environment,
  lang,
  className,
}: {
  site: SiteId;
  environment?: string;
  lang?: 'zh' | 'en';
  className?: string;
}) {
  const s = SITE[site];
  const strings = useUiStrings();
  const name = lang ? s[lang] : (strings.site(site) ?? s.zh);
  return (
    <span
      className={cn(
        'inline-flex h-6 items-center gap-1.5 rounded-md border bg-card px-2 text-xs font-medium whitespace-nowrap',
        className,
      )}
    >
      <span className={cn('size-1.5 rounded-full', s.dot)} aria-hidden />
      <span className={s.text}>{name}</span>
      {environment ? <span className="text-muted-foreground">· {environment}</span> : null}
    </span>
  );
}

/** aitoken.tools 标志：几何小写 a（token 圆 + 工具柄 + 播放三角），墨色底块 + 香槟金字形。 */
export function LogoMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" className={cn('size-6 shrink-0', className)} aria-hidden focusable="false">
      <rect width="32" height="32" rx="8" fill="#14120f" />
      <circle cx="13.25" cy="17.25" r="8.25" fill="#d2b886" />
      <rect x="20.5" y="7.5" width="4.5" height="18" rx="2.25" fill="#d2b886" />
      <path d="M10.6 13.2v8.1l6.6-4.05z" fill="#14120f" />
    </svg>
  );
}

/** 品牌标识：标志 + 字标（aitoken 与降一级灰度的 .tools）。 */
export function BrandMark({ className, label }: { className?: string; label?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-2 font-semibold tracking-tight', className)}>
      <LogoMark />
      {label ?? (
        <span>
          aitoken<span className="text-muted-foreground">.tools</span>
        </span>
      )}
    </span>
  );
}
